/* User-facing points-center API. Never send gateway-owned X-User-Id from the browser. */
(() => {
  'use strict';
  // Java long IDs exceed Number.MAX_SAFE_INTEGER. Preserve them before JSON parsing.
  function parseJSON(text) {
    return JSON.parse(text.replace(/"(?:[^"\\]|\\.)*"|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/g, (token) =>
      /^-?\d{16,}$/.test(token) ? JSON.stringify(token) : token));
  }
  function instant(value) {
    if (!value) return null;
    const date = new Date(typeof value === 'object' ? Number(value.seconds) * 1000 : value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }
  const error = (code) => Object.assign(new Error(code), { code });
  let loginRedirecting = false;
  function loginError(message) {
    const location = window.location;
    const match = location?.pathname?.match(/^(\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?)pages\/points-(?:redemption|coupons)\/?$/i);
    if (match && !window.Shopify?.designMode && !loginRedirecting) {
      loginRedirecting = true;
      try {
        sessionStorage.setItem('points-login-return', location.pathname + location.search + location.hash);
        sessionStorage.setItem('loginToastMessage', typeof message === 'string' ? message : '');
      } catch (_) { /* Login works without storage. */ }
      window.authAPI?.clearAllTokens?.();
      window.userProfileAPI?.clearCachedUserData?.();
      location.replace(match[1] + 'pages/login');
    }
    return Object.assign(error('login'), { rejected: true });
  }
  // Desktop and mobile hubs have separate API instances. Share concurrent reads
  // only; settled requests are removed so retries and later reads stay fresh.
  const readRequests = new Map();
  let couponCacheRevision = 0;
  function sharePendingRequest(key, load) {
    if (!readRequests.has(key)) {
      const pending = Promise.resolve(load()).finally(() => readRequests.delete(key));
      readRequests.set(key, pending);
    }
    return readRequests.get(key);
  }
  function unavailable(reason, details = {}) {
    console.warn('[Points redemption] unavailable', { reason, ...details });
    return Object.assign(error('unavailable'), { reason });
  }
  // These responses explicitly refuse the request. Timeouts, conflicts and
  // rate limits may still describe an in-flight request and must keep its ID.
  const rejected = (code) => [400, 401, 403, 404, 405, 410, 415, 422].includes(Number(code));
  const integer = (value) => typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
  const identifier = (value) => typeof value === 'string' ? /^\d+$/.test(value) : integer(value);
  // Default redemption templates supplied for each storefront market. Keep int64 IDs as strings.
  const defaultTemplateIds = Object.freeze({
    UK: '2094307607513067521',
    EU: '2090332516669206529',
    US: '2110910074079510530'
  });
  function normalizePage(data) {
    if (!data || !Array.isArray(data.records)) return data;
    const page = { ...data };
    for (const key of ['total', 'pageNum', 'pageSize', 'pages']) {
      if (typeof page[key] === 'string' && /^\d+$/.test(page[key]) && integer(Number(page[key]))) page[key] = Number(page[key]);
    }
    return page;
  }
  // Keep template IDs exactly as returned by the list, including across JSON consumers.
  // Coupon IDs retain the existing int64 wire format.
  function requestJSON(body) {
    return JSON.stringify(body).replace(/"(couponId)":"(\d+)"/g, '"$1":$2');
  }
  class PointsAPI {
    static selectedRegion(fallback) {
      // The storefront URL determines the market; a legacy wallet query must
      // not make the displayed store and the requested points region disagree.
      const markets = { us: 'US', eu: 'EU', uk: 'UK', sg: 'Global', ca: 'CA' };
      const market = window.getMarketCode?.();
      if (Object.prototype.hasOwnProperty.call(markets, market)) return markets[market];
      return ['US', 'EU', 'UK', 'Global', 'CA'].includes(fallback) ? fallback : 'US';
    }
    constructor(region) {
      this.region = region; this.balance = null; this.templateLoads = new Map();
      if (typeof window !== 'undefined' && !window.authAPI?.getAccessToken()) loginError();
    }
    get disabledMarket() { return this.region === 'Global' || this.region === 'CA'; }
    async balanceCacheKey() {
      const token = window.authAPI?.getAccessToken();
      if (!token || !globalThis.crypto?.subtle) return null;
      const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(token));
      const owner = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
      return 'points-balance:v1:' + JSON.stringify([window.API_BASE_URL, owner, this.region]);
    }
    validBalance(data) {
      return Boolean(data && integer(data.available) && identifier(data.userId) &&
        /^[A-Z]{3}$/.test(data.currency) && /^(US|UK|EU|SG)$/.test(data.siteRegion) &&
        (!this.region || data.siteRegion === this.region));
    }
    async getCachedBalance() {
      if (this.disabledMarket) return null;
      try {
        const token = window.authAPI?.getAccessToken();
        const key = await this.balanceCacheKey();
        if (!key || token !== window.authAPI?.getAccessToken()) return null;
        const cached = JSON.parse(sessionStorage.getItem(key) || 'null');
        if (!cached || !Number.isFinite(cached.savedAt) || cached.savedAt > Date.now() || Date.now() - cached.savedAt > 5 * 60 * 1000 || !this.validBalance(cached.data)) {
          sessionStorage.removeItem(key);
          return null;
        }
        return { ...cached.data, availablePoints: cached.data.available };
      } catch (_) { return null; }
    }
    async request(path, body) {
      // Global and Canada do not participate in points. Never fall back to the
      // account's primary wallet or read its cache. Both may read cross-store markets.
      if (this.disabledMarket && !(!body && path === '/points/markets')) {
        if (!body && /^\/points\/(transactions|coupons)\?/.test(path)) return { records: [], pages: 0, total: 0, pageNum: 1, pageSize: 5 };
        if (!body && path === '/points/transactions/status-options') return [];
        if (!body && path === '/points/markets') return [];
        throw error('unavailable');
      }
      if (body) return this.performRequest(path, body);
      const key = JSON.stringify([window.API_BASE_URL, window.authAPI?.getAccessToken(),
        window.getRequestAcceptLanguage?.() || document.documentElement.lang || 'en', path]);
      return sharePendingRequest(key, () => this.performRequest(path));
    }
    async performRequest(path, body) {
      if (this.disabledMarket && !(!body && path === '/points/markets')) throw error('unavailable');
      const token = window.authAPI?.getAccessToken();
      if (!token) throw loginError();
      const base = window.API_BASE_URL;
      if (!base) throw unavailable('api_base_url_missing', { path, siteRegion: this.region });
      const headers = { Authorization: `Bearer ${token}`, 'X-Client-Type': 'web',
        'Accept-Language': window.getRequestAcceptLanguage?.() || document.documentElement.lang || 'en' };
      if (body) headers['Content-Type'] = 'application/json';
      const moduleBase = base.replace(/\/+$/, '').replace(/\/points-center$/, '') + '/points-center';
      const response = await fetch(moduleBase + path, {
        method: body ? 'POST' : 'GET', headers, ...(body ? { body: requestJSON(body) } : {}),
        // The shared transport owns a fresh timeout for each read attempt.
        ...(body ? { signal: AbortSignal.timeout(20000) } : { timeoutMs: 20000 })
      });
      let result;
      try { result = parseJSON(await response.text()); }
      catch (_) {
        if (response.status === 401) throw loginError();
        throw Object.assign(error('load_error'), { rejected: rejected(response.status) });
      }
      // Read the backend message before redirecting. HTTP 401 still takes
      // precedence over any conflicting response-body business code.
      if (response.status === 401 || Number(result?.code) === 401) throw loginError(result?.message);
      if (result && Object.prototype.hasOwnProperty.call(result, 'code')) {
        // Any non-200 business code is a final failure: allow a new manual redemption.
        if (Number(result.code) !== 200) throw Object.assign(error('failed'), {
          rejected: true,
          backendMessage: typeof result.message === 'string' ? result.message.trim() : ''
        });
        return normalizePage(result.data);
      }
      if (!response.ok) throw Object.assign(error('load_error'), { rejected: rejected(response.status) });
      return normalizePage(result);
    }
    query(params = {}) {
      return '?' + new URLSearchParams({ ...(this.region ? { siteRegion: this.region } : {}), ...params });
    }
    async getMarkets() {
      const data = await this.request('/points/markets');
      if (!Array.isArray(data) || data.some(market => !market ||
          !/^(US|UK|EU|SG)$/.test(market.siteRegion) || !integer(market.available) ||
          !integer(market.pending)) || new Set(data.map(market => market.siteRegion)).size !== data.length) throw error('load_error');
      return data;
    }
    async getBalance() {
      if (this.disabledMarket) return { available: 0, availablePoints: 0, pending: 0, siteRegion: this.region };
      const key = await this.balanceCacheKey().catch(() => null);
      const data = await this.request('/points/balance' + this.query());
      if (!this.validBalance(data)) throw error('load_error');
      this.balance = { ...data, availablePoints: data.available };
      this.region = data.siteRegion;
      try { if (key) sessionStorage.setItem(key, JSON.stringify({ savedAt: Date.now(), data })); } catch (_) { /* Storage is optional. */ }
      return this.balance;
    }
    async getTransactionStatusOptions() {
      const data = await this.request('/points/transactions/status-options');
      if (!Array.isArray(data) || data.some(option => !option || typeof option.code !== 'string' || !option.code.trim() || typeof option.label !== 'string' || !option.label.trim()) || new Set(data.map(option => option.code)).size !== data.length) throw error('load_error');
      return data;
    }
    async getTransactions(pageNum = 1, { status, txType } = {}) {
      const filters = {};
      if (status) {
        if (typeof status !== 'string') throw error('load_error');
        filters.status = status;
      }
      if (txType) filters.txType = txType;
      const data = await this.request('/points/transactions' + this.query({ pageNum, pageSize: 5, ...filters }));
      if (!data || !Array.isArray(data.records) || data.records.length > 5 || !integer(data.pages) || data.records.some(r => !Number.isSafeInteger(r.pointsDelta))) throw error('load_error');
      return data;
    }
    loadCouponTemplates() {
      if (this.disabledMarket) return Promise.resolve([]);
      const region = this.region;
      const templateId = defaultTemplateIds[region];
      if (identifier(templateId)) return Promise.resolve([
        { templateId, amountMode: 'DYNAMIC', discountType: 'FIXED' }
      ]);
      if (this.templateLoads.has(region)) return this.templateLoads.get(region);
      const pending = this.fetchCouponTemplates(region).catch((err) => {
        this.templateLoads.delete(region);
        throw err;
      });
      this.templateLoads.set(region, pending);
      return pending;
    }
    async fetchCouponTemplates(region) {
      const records = [], seen = new Set();
      for (let pageNum = 1; pageNum <= 100; pageNum++) {
        const data = await this.request('/points/coupon-templates' + this.query({ siteRegion: region, pageNum, pageSize: 100 }));
        if (!Array.isArray(data?.records) || (data.pages != null && (!integer(data.pages) || data.pages > 100))) throw error('load_error');
        for (const item of data.records) {
          if (!identifier(item.templateId) || seen.has(String(item.templateId))) throw error('load_error');
          seen.add(String(item.templateId)); records.push(item);
        }
        if (pageNum >= (data.pages || 1)) break;
        if (!data.records.length) throw error('load_error');
      }
      return records;
    }
    async quote({ points }) {
      const balance = await this.getBalance();
      if (!integer(points) || points < 1) throw error('integer');
      if (points > balance.available) throw error('exceeds');
      const records = await this.loadCouponTemplates();
      const templates = records.filter(t => t.amountMode === 'DYNAMIC' && t.discountType === 'FIXED' &&
        identifier(t.templateId) && (!t.status || t.status === 'PUBLISHED') &&
        (!t.fixedExpireAt || new Date(instant(t.fixedExpireAt)).getTime() > Date.now()));
      // Multiple available templates must not block redemption. Use API ordering.
      const templateDiagnostics = {
        siteRegion: this.region,
        returnedCount: records.length,
        eligibleCount: templates.length,
        templates: records.map(t => ({
          templateId: String(t.templateId), amountMode: t.amountMode,
          discountType: t.discountType, status: t.status,
          fixedExpireAt: instant(t.fixedExpireAt),
          validId: identifier(t.templateId),
          eligible: templates.includes(t)
        }))
      };
      console.debug('[Points redemption] template selection', templateDiagnostics);
      if (!templates.length) throw unavailable('no_eligible_templates', templateDiagnostics);
      const template = templates[0];
      if ((template.minRedeemPoints != null && points < template.minRedeemPoints) ||
          (template.maxRedeemPoints != null && points > template.maxRedeemPoints)) throw error('range');
      return { quoteId: String(template.templateId), templateId: String(template.templateId),
        points, value: points, currency: balance.currency, remaining: balance.available - points,
        siteRegion: balance.siteRegion, userId: String(balance.userId), template };
    }
    pendingKey() {
      if (!this.balance) throw error('load_error');
      return `points-redemption:${this.balance.userId}:${this.balance.siteRegion}`;
    }
    pending() { return this.disabledMarket ? null : JSON.parse(localStorage.getItem(this.pendingKey()) || 'null'); }
    async redeem(quote, recover = false) {
      // A durable request plus a cross-tab lock prevents duplicate submissions, including reloads.
      if (!navigator.locks || !crypto.randomUUID) throw unavailable('browser_capabilities_missing', {
        locksSupported: Boolean(navigator.locks), uuidSupported: Boolean(crypto.randomUUID),
        secureContext: globalThis.isSecureContext
      });
      const key = this.pendingKey();
      return navigator.locks.request(key, { ifAvailable: true }, async (lock) => {
        if (!lock) throw error('unknown');
        await this.getBalance();
        if (this.pendingKey() !== key) throw error('login');
        let pending = this.pending();
        if (pending && !recover) throw error('unknown');
        if (!pending) {
          if (recover || !quote || quote.userId !== String(this.balance.userId)) throw error('load_error');
          pending = { templateId: String(quote.templateId), pointsAmount: quote.points,
            siteRegion: quote.siteRegion, requestId: crypto.randomUUID() };
          localStorage.setItem(key, JSON.stringify(pending));
        }
        // Normalize saved requests too, so recovery always sends a string template ID.
        pending.templateId = String(pending.templateId);
        localStorage.setItem(key, JSON.stringify(pending));
        let result;
        try { result = await this.request('/points/redeem', pending); }
        catch (failure) {
          // Business failures are final; failures without a business result retain the request ID.
          if (failure.rejected) localStorage.removeItem(key);
          throw failure;
        }
        if (!result || !identifier(result.couponId) || !result.discountCode || !integer(result.remainingBalance)) throw error('unknown');
        try {
          const cacheKey = await this.balanceCacheKey();
          if (cacheKey) sessionStorage.removeItem(cacheKey);
        } catch (_) { /* Storage is optional. */ }
        this.clearCouponCache();
        localStorage.removeItem(key);
        return result;
      });
    }
    async couponCacheKey(pageNum, status) {
      const owner = await this.balanceCacheKey();
      return owner && 'points-coupons:v1:' + JSON.stringify([owner,
        window.getRequestAcceptLanguage?.() || document.documentElement.lang || 'en', pageNum, status]);
    }
    async getCachedCoupons(pageNum = 1, status = '') {
      if (this.disabledMarket) return null;
      try {
        const token = window.authAPI?.getAccessToken();
        const key = await this.couponCacheKey(pageNum, status);
        if (!key || token !== window.authAPI?.getAccessToken()) return null;
        const cached = JSON.parse(sessionStorage.getItem(key) || 'null');
        if (!cached || !Number.isFinite(cached.savedAt) || cached.savedAt > Date.now() || Date.now() - cached.savedAt >= 300000) {
          sessionStorage.removeItem(key); return null;
        }
        return this.normalizeCoupons(cached.data);
      } catch (_) { return null; }
    }
    clearCouponCache() {
      couponCacheRevision++;
      try {
        for (let i = sessionStorage.length - 1; i >= 0; i--) {
          const key = sessionStorage.key(i);
          if (key?.startsWith('points-coupons:v1:')) sessionStorage.removeItem(key);
        }
      } catch (_) { /* Storage is optional. */ }
    }
    async listCoupons(pageNum = 1, status = '') {
      if (this.disabledMarket) return { records: [], pages: 0, total: 0, pageNum, pageSize: 5 };
      if (status && !['AVAILABLE', 'USED', 'EXPIRED', 'RECALLED'].includes(status)) throw error('load_error');
      const token = window.authAPI?.getAccessToken();
      const cacheRevision = couponCacheRevision;
      const key = await this.couponCacheKey(pageNum, status).catch(() => null);
      const path = '/points/coupons' + this.query({ pageNum, pageSize: 5, ...(status ? { status } : {}) });
      const data = await sharePendingRequest(JSON.stringify(['coupon-list', token, key, path, cacheRevision]), () => this.performRequest(path));
      if (cacheRevision !== couponCacheRevision) throw error('load_error');
      const normalized = this.normalizeCoupons(data);
      try { if (key) sessionStorage.setItem(key, JSON.stringify({ savedAt: Date.now(), data })); } catch (_) { /* Storage is optional. */ }
      return normalized;
    }
    normalizeCoupons(data) {
      if (!data || !Array.isArray(data.records) || data.records.length > 5 || !integer(data.pages)) throw error('load_error');
      const seen = new Set();
      const records = data.records.map(coupon => {
        if (!identifier(coupon.couponId) || seen.has(String(coupon.couponId))) throw error('load_error');
        seen.add(String(coupon.couponId));
        const validUntil = instant(coupon.expireAt);
        const state = coupon.status === 'AVAILABLE' && (coupon.expired || (validUntil && Date.parse(validUntil) <= Date.now())) ? 'expired' : String(coupon.status).toLowerCase();
        if (!['available', 'used', 'expired', 'recalled'].includes(state) || !Number.isFinite(coupon.discountValue) || !/^[A-Z]{3}$/.test(coupon.currency)) throw error('load_error');
        return { ...coupon, status: state, value: coupon.discountValue, code: coupon.discountCode,
          validUntil, usedAt: instant(coupon.usedAt), orderNumber: coupon.orderNumber || coupon.orderName ||
            (typeof coupon.orderId === 'string' && !/^\d+$|^gid:\/\//.test(coupon.orderId) ? coupon.orderId : null) };
      });
      return { ...data, records };
    }
    async getCouponDetails(couponId) {
      if (!identifier(couponId)) throw error('load_error');
      const data = await this.request('/points/coupons/detail?' + new URLSearchParams({ couponId: String(couponId) }));
      if (!data || String(data.couponId) !== String(couponId)) throw error('load_error');
      return data;
    }
    async useCoupon(couponId, requestId) {
      if (!identifier(couponId) || !requestId) throw error('load_error');
      const data = await this.request('/points/coupons/use', { couponId: String(couponId), requestId });
      if (!data || String(data.couponId) !== String(couponId) || !data.discountCode || !data.redirectUrl) throw error('load_error');
      this.clearCouponCache();
      return data;
    }
  }
  PointsAPI.instant = instant;
  if (typeof module !== 'undefined' && module.exports) module.exports = { PointsAPI, parseJSON, instant };
  if (typeof window !== 'undefined') window.PointsAPI = PointsAPI;
})();
